import { Fragment, useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import OnboardCpaSteps from "../../ui/steps/OnboardCpaSteps";
import { updateUserStatus } from "../../../store/redux_auth/authSlice";
import ConnectedAccountFlow from "../../payments/ConnectedAccountFlow";
import { updateAccount } from "../../../api/account/accountAPI";
import { getStripeConnectedAccount } from "../../../services/payments/onboardConnectedAccount";
import LoadingPlaceholder from "../../ui/loading/LoadingPlaceholder";
import SmallAlert from "../../ui/alerts/SmallAlert";
import "../../../styles/v2/layouts/internal-forms.css";
import { useRefreshOnAuth } from "../../../hooks/useRefreshOnAuth";

function ConnectServiceProviderAccount() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state - must be called before any conditional return
  const { user } = useSelector((state) => state.auth);
  const cpaStatus = user?.account_status;

  const [licenseNotification, setLicenseNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState(null);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);

  // Check connected account status on mount
  const checkConnectedAccount = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getStripeConnectedAccount(
        "payments/create-connected-account",
      );
      if (response.ok) {
        const data = await response.json();
        const status = {
          hasAccount: data.hasConnectedAccount,
          payoutsEnabled: data.account_info?.payoutsEnabled,
          chargesEnabled: data.account_info?.chargesEnabled,
          rejected: data.account_info?.rejected,
          pendingVerification: data.account_info?.pendingVerification,
        };
        setAccountStatus(status);

        // If account is already active and CPA is CA, update to LE and show completion
        if (status.hasAccount && status.payoutsEnabled && cpaStatus === "CA") {
          const response = await updateAccount({ account_status: "LE" });
          if (response.ok) {
            dispatch(updateUserStatus({ account_status: "LE" }));
          }
          setShowCompletionScreen(true);
        }
      }
    } catch (err) {
      console.error("Error checking connected account:", err);
    } finally {
      setLoading(false);
    }
  }, [cpaStatus, dispatch]);

  // This side effect is used for: (a) Refresh page (b) When render the page for first
  useRefreshOnAuth(checkConnectedAccount);

  useEffect(() => {
    checkConnectedAccount();
  }, [checkConnectedAccount]);

  // Handle completion after Stripe onboarding - update status to LE and show completion screen
  const handleStripeComplete = async () => {
    const response = await updateAccount({ account_status: "LE" });
    if (response.ok) {
      dispatch(updateUserStatus({ account_status: "LE" }));
    }
    setShowCompletionScreen(true);
  };

  // Handle button click to go to next step
  const handleGoToNextStep = () => {
    navigate("/onboard");
  };

  // Check for license validation notification from previous page
  useEffect(() => {
    const notification = sessionStorage.getItem(
      "license_validation_notification",
    );
    if (notification) {
      try {
        const notificationData = JSON.parse(notification);
        // Only show if notification is recent (within last 30 seconds)
        if (Date.now() - notificationData.timestamp < 30000) {
          setLicenseNotification(notificationData);
        }
        // Clear the notification from sessionStorage
        sessionStorage.removeItem("license_validation_notification");
      } catch (e) {
        console.error("Error parsing license notification:", e);
      }
    }
  }, []);

  // Redirect if user shouldn't be on this step
  // Connected Account step is only for status "CA" or "LE"
  if (cpaStatus && cpaStatus !== "CA" && cpaStatus !== "LE") {
    return <Navigate to="/onboard" replace />;
  }

  if (loading) {
    return (
      <div className="main-content-area">
        <div className="content">
          <OnboardCpaSteps />
          <div className="content-wrapper">
            <LoadingPlaceholder rows={1} type="card-large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="main-content-area">
        <div className="content">
          <OnboardCpaSteps />

          {/* Show license validation notification if available */}
          {licenseNotification && (
            <div style={{ marginBottom: "20px" }}>
              <SmallAlert
                alert="True"
                type={licenseNotification.type}
                title={licenseNotification.title}
                message={licenseNotification.message}
                onDismiss={() => setLicenseNotification(null)}
                dismissOnFocusOutside={true}
              />
            </div>
          )}

          {showCompletionScreen ? (
            // Show completion screen (after creating new account OR if already had active account)
            <div className="content-wrapper">
              <div className="review-status-container">
                <img
                  src={`${process.env.PUBLIC_URL}/images/review_status.svg`}
                  alt="Connected Account"
                  style={{ width: "auto", height: "auto" }}
                />
                <div className="review-status-content">
                  <h2 className="review-status-title">
                    Payout Account Connected!
                  </h2>
                  <p className="review-status-text">
                    Your payout account is set up and ready. Continue to
                    complete your identity verification.
                  </p>
                </div>
              </div>
              <div className="step-navigation">
                <button
                  type="button"
                  className="cta-button primary"
                  onClick={handleGoToNextStep}
                >
                  Continue to Identity Verification
                </button>
              </div>
            </div>
          ) : accountStatus?.hasAccount && accountStatus?.rejected ? (
            // Connected account rejected by Stripe
            <div className="content-wrapper">
              <div className="review-status-container">
                <img
                  src={`${process.env.PUBLIC_URL}/images/review_status.svg`}
                  alt="Account Rejected"
                  style={{ width: "auto", height: "auto" }}
                />
                <div className="review-status-content">
                  <h2 className="review-status-title">
                    Payout Account Requires Attention
                  </h2>
                  <p className="review-status-text">
                    Your connected account was not approved by Stripe. Please
                    contact our support team for assistance.
                  </p>
                </div>
              </div>
              <div className="step-navigation">
                <button
                  type="button"
                  className="cta-button primary"
                  onClick={() => navigate("/help-center")}
                >
                  Contact Support
                </button>
              </div>
            </div>
          ) : accountStatus?.hasAccount &&
            accountStatus?.pendingVerification ? (
            // Connected account pending requirements
            <div className="content-wrapper">
              <div className="review-status-container">
                <img
                  src={`${process.env.PUBLIC_URL}/images/review_status.svg`}
                  alt="Pending Verification"
                  style={{ width: "auto", height: "auto" }}
                />
                <div className="review-status-content">
                  <h2 className="review-status-title">
                    Payout Account Pending
                  </h2>
                  <p className="review-status-text">
                    Your connected account has pending requirements. Please
                    complete the verification process with Stripe.
                  </p>
                </div>
              </div>
              <div className="step-navigation">
                <button
                  type="button"
                  className="cta-button primary"
                  onClick={() => window.location.reload()}
                >
                  Complete Verification
                </button>
              </div>
            </div>
          ) : (
            // No connected account - show Stripe Connect flow
            <ConnectedAccountFlow
              onComplete={handleStripeComplete}
              title="Get paid, securely and on time."
              description="To start accepting payments, connect your Stripe account."
            />
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default ConnectServiceProviderAccount;
